import { useNavigate } from '@tanstack/react-router';
import { Loader, MoreHorizontal } from 'lucide-react';
import { overlay } from 'overlay-kit';

import { QueryFilterEditModal } from '../../../entities/query-builder/QueryFilterEditModal';
import { QueryStatusModal } from '../../../entities/query-builder/QueryStatusModal';
import { useDeleteQuery } from '../../../entities/query/mutation/use-delete-query';

import { showDeleteQueryConfirm } from '../overlay/show-delete-query-confirm';

import { getPlatformLogo } from '@/entities/public-filter/constants';
import { useQueryProgress } from '@/entities/query/query/use-query-progress';
import type { SavedQuery } from '@/entities/query/types';
import { USER_ROLE } from '@/entities/user/constants';
import { WORKSPACE_PLAN } from '@/entities/workspace/constants';
import { Badge } from '@/shared/components/shadcn/badge';
import { Button } from '@/shared/components/shadcn/button';
import { Card } from '@/shared/components/shadcn/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/shadcn/dropdown-menu';
import { Progress } from '@/shared/components/shadcn/progress';
import { Stage } from '@/shared/components/stage/stage';
import { useWorkspace } from '@/shared/hooks/use-workspace';

interface Props {
  savedQuery: SavedQuery;
}

export function SavedQueryCard({ savedQuery }: Props) {
  const { workspace } = useWorkspace();
  const workspaceId = workspace.id;

  const navigate = useNavigate();
  const deleteQuery = useDeleteQuery();
  const queryProgress = useQueryProgress(workspaceId, savedQuery.id, savedQuery.type === 'private');

  const handleCardClick = () => {
    navigate({
      to: '/workspace/$workspaceId/insight',
      params: { workspaceId },
      search: { q: savedQuery.id },
    });
  };

  const getTotalProgress = () => {
    const { total, progress } = queryProgress.reduce(
      (acc, item) => ({
        total: acc.total + 100,
        progress: acc.progress + item.progress,
      }),
      {
        total: 0,
        progress: 0,
      },
    );
    if (total === 0) return 0;
    if (progress === total) return 100;
    if (progress === 0) return 0;
    return Math.round((progress / total) * 100);
  };

  return (
    <Card className="group flex flex-row items-center justify-between p-0 transition-shadow hover:shadow-md">
      <div
        className="flex flex-1 cursor-pointer flex-col gap-4 px-6 py-6"
        onClick={handleCardClick}
      >
        <h3 className="flex gap-3">
          {savedQuery.type === 'public' && <Badge className="h-5">Sample</Badge>}
          <span className="text-base font-semibold">{savedQuery.name}</span>
        </h3>

        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2.5">
            {queryProgress.map((item) => {
              try {
                const PlatformLogo = getPlatformLogo(item.platform);
                return (
                  <PlatformLogo key={item.platform} platform={item.platform} className="size-4" />
                );
              } catch (e) {
                console.error(e);
                return null;
              }
            })}
          </div>

          <Stage>
            <Stage.Scene status={queryProgress.some((item) => item.status === 'running')}>
              <div className="flex items-center gap-2">
                <div className="mx-2 h-4 border" />
                <GeneratingIndicator progress={getTotalProgress()} />
              </div>
            </Stage.Scene>
          </Stage>
        </div>
      </div>
      <div className="flex gap-2 px-6 py-6">
        <Button
          variant="outline"
          disabled={
            savedQuery.type === 'public' ||
            queryProgress.some((item) => item.status === 'running') ||
            workspace?.privilege === USER_ROLE.VIEWER
          }
          onClick={() => {
            overlay.open(({ isOpen, close }) => (
              <QueryFilterEditModal
                isOpen={isOpen}
                onClose={close}
                unmount={close}
                savedQuery={savedQuery}
                workspaceId={workspaceId}
              />
            ));
          }}
        >
          Edit filter
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon">
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              disabled={savedQuery.type === 'public' || deleteQuery.isPending}
              onClick={() => {
                overlay.open(({ isOpen, close }) => (
                  <QueryStatusModal
                    isOpen={isOpen}
                    onClose={close}
                    unmount={close}
                    queryId={savedQuery.id}
                    workspaceId={workspaceId}
                  />
                ));
              }}
            >
              Check AI status
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              disabled={
                savedQuery.type === 'public' ||
                deleteQuery.isPending ||
                workspace?.privilege === USER_ROLE.VIEWER
              }
              onClick={async () => {
                const action = await showDeleteQueryConfirm(
                  savedQuery,
                  workspace?.plan === WORKSPACE_PLAN.TRIAL,
                );

                if (action === 'confirm') {
                  await deleteQuery.mutateAsync({
                    workspaceId,
                    queryId: savedQuery.id,
                  });
                }
              }}
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </Card>
  );
}

function GeneratingIndicator({ progress }: { progress: number }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-muted-foreground">Generating</span>
      <span className="text-muted-foreground">{progress}%</span>
      <Progress value={progress} className="h-4 w-[400px]" />
      <Loader className="text-muted-foreground size-4 animate-spin" />
    </div>
  );
}
